import { useState, useEffect, useRef } from "react";
import { Bell, Users, Calendar, RefreshCw, CheckCircle2, Loader2, X, DollarSign, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth, rolePrefix } from "../../lib/AuthContext";
import { useNavigate } from "react-router-dom";

/* NotificationsDropdown — bell icon with dropdown panel.
   Fetches /v1/notifications/today, polls every 60s.
   Employee: leads + follow-ups + status changes on their leads.
   Admin/Super: everything + attendance + bookings. */

// Format IST time from ISO string
function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

// "2 hours ago" style relative time
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Section icon by key
const SECTION_ICONS = {
  leads: Users,
  followups: Calendar,
  status: RefreshCw,
  attendance: CheckCircle2,
  bookings: DollarSign,
};

// Human-readable message per section item
function messageFor(sectionKey, item) {
  switch (sectionKey) {
    case "leads":
      return {
        title: item.name,
        subtitle: item.project ? `Inquired about ${item.project}` : "New lead captured",
        time: timeAgo(item.createdAt),
      };
    case "followups":
      return {
        title: `Follow up with ${item.name}`,
        subtitle: item.project || "Scheduled call",
        time: `at ${formatTime(item.followUpAt)}`,
      };
    case "status":
      const from = item.fromStatus || "new";
      const to = item.toStatus;
      const by = item.changedBy?.name || "someone";
      return {
        title: item.lead?.name || "A lead",
        subtitle: `Moved from ${from} to ${to} by ${by}`,
        time: timeAgo(item.createdAt),
      };
    case "attendance":
      const empName = item.employee?.user?.name || "An employee";
      const clockIn = item.clockInAt ? `at ${formatTime(item.clockInAt)}` : "";
      return {
        title: empName,
        subtitle: `Marked ${item.status} ${clockIn}`.trim(),
        time: item.clockInAt ? timeAgo(item.clockInAt) : "today",
      };
    case "bookings":
      const price = item.totalCost ? `₹${(Number(item.totalCost) / 100000).toFixed(1)}L` : "";
      return {
        title: item.clientName,
        subtitle: `Booked ${item.config || ""} at ${item.projectName}`.trim(),
        time: `${price} · ${timeAgo(item.createdAt)}`.replace(/^ · /, ""),
      };
    default:
      return { title: "Update", subtitle: "", time: "" };
  }
}

export default function NotificationsDropdown() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, sections: [] });
  // Only these two sections are surfaced to the user.
  const VISIBLE_SECTIONS = ["leads", "followups"];
  /* Dismissal lives on the SERVER now, in notification_reads, keyed by user
     and "section:recordId". It used to be a Set in localStorage -- per
     browser and per origin, so clearing on desktop left everything untouched
     in the APK, and logging out and back in changed nothing either way.
     Three silos for one person.
    
     The feed arrives already filtered, so there is no client-side Set to
     keep. Dismissing is a round trip then a refetch: a beat slower than
     localStorage was, but the same everywhere the user signs in.
    
     Anything dismissed before this shipped is stranded in localStorage and
     reappears once. Dismissing it again records it properly. */
  const [error, setError] = useState(null);
  const panelRef = useRef(null);

  async function fetchNotifications() {
    setLoading(true);
    setError(null);
    try {
      // Cache-bust with timestamp — mobile WebViews cache aggressively.
      const res = await api.get(`/notifications/today?t=${Date.now()}`);
      setData(res);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  // Initial fetch + polling every 60 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Close on click outside — use both mousedown (desktop) and touchstart (mobile).
  // Small setTimeout so the initial bell tap doesn't immediately close the panel.
  useEffect(() => {
    if (!open) return;
    function onOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("touchstart", onOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  function handleItemClick(sectionKey, item) {
    const prefix = user ? rolePrefix(user.role) : "";
    setOpen(false);
    // Route to relevant page based on section
    if (sectionKey === "leads" || sectionKey === "followups" || sectionKey === "status") {
      const leadId = item.id || item.lead?.id;
      if (leadId) {
        window.dispatchEvent(new CustomEvent("abhighar:open-lead", { detail: { leadId } }));
        setOpen(false);
      } else {
        navigate(`${prefix}/leads/all`);
        setOpen(false);
      }
    } else if (sectionKey === "attendance") {
      navigate(`${prefix}/attendance/roster`);
    } else if (sectionKey === "bookings") {
      navigate(`${prefix}/billing/bookings`);
    }
  }

  // Filter to the two surfaced sections, and drop items cleared earlier.
  const visibleSections = (data.sections || [])
    .filter((sec) => VISIBLE_SECTIONS.includes(sec.key))
    .map((sec) => {
      const items = sec.items || [];   // server already filtered dismissals
      return { ...sec, items, count: items.length };
    })
    .filter((sec) => sec.count > 0);
  const visibleCount = visibleSections.reduce((n, sec) => n + sec.count, 0);
  const hasItems = visibleCount > 0;

  /* Dismiss ONE notification. Same store, key and 300-item cap as
     clearNotifications -- notifications are derived server-side rather than
     stored, so there is no row to flag; the client keeps a set of ids it has
     already seen and filters them out at render.

     Per-browser, like Clear all has always been: marking read on desktop
     does not clear it in the APK, which is a separate WebView with its own
     storage. Syncing would need a table keyed by user and notification id,
     plus confidence that the derived ids stay stable between requests. */
  const markRead = async (sectionKey, id) => {
    if (!id) return;
    try {
      await api.post("/notifications/read", { ids: [`${sectionKey}:${id}`] });
      await fetchNotifications();
    } catch { /* leave it visible rather than hiding something still unread */ }
  };

  const clearNotifications = async () => {
    const ids = [];
    for (const sec of visibleSections) {
      for (const it of (sec.items || [])) {
        if (it && it.id) ids.push(`${sec.key}:${it.id}`);
      }
    }
    if (ids.length === 0) return;
    try {
      await api.post("/notifications/read", { ids });
      await fetchNotifications();
    } catch { /* nothing dismissed; the list stands */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative text-slate-400 hover:text-slate-700"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {hasItems && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
            {visibleCount > 99 ? "99+" : visibleCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute top-16 sm:top-auto left-2 right-2 sm:left-auto sm:right-0 sm:mt-2 sm:w-[380px] max-h-[calc(100vh-96px)] sm:max-h-[calc(100vh-120px)] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-slate-700" />
              <span className="font-semibold text-slate-900 text-sm">Notifications</span>
              {visibleCount > 0 && (
                <span className="text-xs text-slate-500">({visibleCount})</span>
              )}
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1">
            {loading && !data.total && (
              <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading…
              </div>
            )}

            {error && (
              <div className="p-4 text-sm text-red-600">{error}</div>
            )}

            {!loading && !error && !hasItems && (
              <div className="py-8 text-center text-slate-500 text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No notifications today
              </div>
            )}

            {visibleSections.map((section) => {
              const Icon = SECTION_ICONS[section.key] || Bell;
              return (
                <div key={section.key} className="border-b border-slate-100 last:border-b-0">
                  <div className="px-4 py-2 bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    <Icon className="w-3.5 h-3.5" />
                    {section.title}
                    <span className="text-slate-400 normal-case font-normal">({section.count})</span>
                  </div>
                  <div>
                    {section.items.map((item) => {
                      const msg = messageFor(section.key, item);
                      return (
                        <div
                          key={item.id}
                          className="group relative flex items-start border-b border-slate-50 last:border-b-0 hover:bg-slate-50"
                        >
                          <button
                            onClick={() => handleItemClick(section.key, item)}
                            className="flex-1 min-w-0 px-4 py-3 text-left"
                          >
                            <div className="text-sm font-medium text-slate-900 truncate">{msg.title}</div>
                            <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{msg.subtitle}</div>
                            <div className="text-[11px] text-slate-400 mt-1">{msg.time}</div>
                          </button>
                          {/* Always visible on touch, where there is no hover
                              to reveal it. */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); markRead(section.key, item.id); }}
                            title="Mark as read"
                            aria-label="Mark as read"
                            className="shrink-0 self-center mr-2 p-1.5 rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-200/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer: refresh + clear */}
          <div className="px-4 py-2 border-t border-slate-100 shrink-0 flex items-center justify-between gap-2">
            <button
              onClick={fetchNotifications}
              disabled={loading}
              className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={clearNotifications}
              disabled={!hasItems}
              className="text-xs text-slate-600 hover:text-red-600 flex items-center gap-1 disabled:opacity-40 disabled:hover:text-slate-600"
            >
              <Trash2 className="w-3 h-3" />
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
